import { Clock, Info } from "lucide-react";

import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import type { BrebReceivingKey } from "@/shared/services/payments/brebReceivingKey.service";
import { formatLimitAmount } from "@/shared/utils/amountLimits";

import { BrebKeyPlate } from "./BrebKeyPlate";
import { BrebKeyStaticQr } from "./BrebKeyStaticQr";

export interface BrebKeyPanelProps {
  receivingKey: BrebReceivingKey;
}

export function BrebKeyPanel({ receivingKey }: BrebKeyPanelProps) {
  const { t, activeLanguage } = useTranslations("payins");
  const pending = receivingKey.status === "PENDING";
  const staticQrPayload = pending ? null : receivingKey.static_qr_payload;
  const generatingQr = receivingKey.status === "ACTIVE" && !receivingKey.static_qr_payload;
  const min = pending ? null : receivingKey.min_amount;
  const max = pending ? null : receivingKey.max_amount;
  const format = (value: string) => formatLimitAmount(value, "COP", activeLanguage);
  const hasQrColumn = !!staticQrPayload || generatingQr;

  return (
    <div className="@container space-y-4">
      {pending && (
        <ColocaAlert
          variant="secondary"
          icon={Clock}
          heading={t("breb_key_pending_title")}
          text={t("breb_key_pending_body")}
        />
      )}

      <div className={cn("grid gap-4", hasQrColumn && "@2xl:grid-cols-2 @2xl:items-start")}>
        <div className="space-y-4">
          <BrebKeyPlate
            keyValue={receivingKey.key_value}
            holderName={pending ? null : receivingKey.holder_name}
            showActions={!pending}
            pending={pending}
          />

          {(min || max) && (
            <section
              aria-label={t("breb_key_bounds_title")}
              className="rounded-2xl border border-border bg-card p-4"
            >
              <dl className="flex flex-wrap gap-x-8 gap-y-2">
                {min && (
                  <div>
                    <dt className="text-xs text-muted-foreground">{t("breb_key_bounds_min")}</dt>
                    <dd className="font-mono text-sm font-semibold text-foreground">{format(min)}</dd>
                  </div>
                )}
                {max && (
                  <div>
                    <dt className="text-xs text-muted-foreground">{t("breb_key_bounds_max")}</dt>
                    <dd className="font-mono text-sm font-semibold text-foreground">{format(max)}</dd>
                  </div>
                )}
              </dl>
              <p className="mt-2 text-xs text-muted-foreground">{t("breb_key_bounds_hint")}</p>
            </section>
          )}

          {!pending && (
            <section className="rounded-2xl border border-border bg-card p-4">
              <h3 className="text-sm font-semibold text-foreground">{t("breb_key_howto_title")}</h3>
              <ol className="mt-3 space-y-2.5">
                {[t("breb_key_howto_step1"), t("breb_key_howto_step2"), t("breb_key_howto_step3")].map(
                  (step, index) => (
                    <li key={step} className="flex gap-3 text-sm text-foreground">
                      <span
                        aria-hidden="true"
                        className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 font-mono text-xs font-semibold text-primary"
                      >
                        {index + 1}
                      </span>
                      <span className="pt-0.5">{step}</span>
                    </li>
                  ),
                )}
              </ol>
            </section>
          )}
        </div>
        {hasQrColumn && (
          <section className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold text-foreground">{t("breb_key_static_qr_title")}</h3>
              {staticQrPayload && (
                <p className="mt-1 text-sm text-muted-foreground">{t("breb_key_static_qr_body")}</p>
              )}
            </div>
            <BrebKeyStaticQr receivingKey={receivingKey} />
          </section>
        )}
      </div>

      {!pending && (
        <ColocaAlert
          variant="secondary"
          icon={Info}
          heading={t("breb_key_qr_title")}
          text={`${t("breb_key_qr_body")} ${t("breb_key_qr_fallback")}`}
        />
      )}
    </div>
  );
}
